/**
 * Scan dialog: choose a target and a frequency, see the price, pay.
 *
 * COST
 * Credits are charged the moment the schedule is created, for EVERY run in it,
 * and are not refunded. So this dialog states the exact charge, the balance
 * before and after, and the number of runs before anything happens, and it
 * cannot be confirmed until (a) the price has actually loaded, (b) the balance
 * covers it, and (c) the person has ticked an explicit acknowledgement naming
 * the amount. The server refuses without `acknowledged: true` as well.
 *
 * If the price cannot be loaded the dialog does not guess and does not allow a
 * charge: spending credits without seeing the price is the failure to avoid.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  SCAN_FREQUENCIES, checkRootDomain, normalizeDomain, type ScanFrequency,
} from '@opencomply/shared';
import { api, qs } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/cn';
import { Modal, Button, Field, ErrorNotice, Skeleton } from '../ui';
import { IconShield } from '../icons';
import { useScanT, useScanError, useCredits } from './strings';

export interface Pricing {
  balance: number;
  unitPrice: number;
  packages: Array<{
    frequency: ScanFrequency; runs: number; totalCost: number;
    balanceAfter: number; affordable: boolean;
  }>;
}

export interface ScheduleResult {
  config: { id: string };
  balance: number;
  runs: number;
  creditsCharged: number;
}

interface VendorOption { id: string; name: string; domain: string; isActive: boolean }

const OWN = '__own__';

export function ScheduleScanModal({ open, onClose, onScheduled, initialDomain, initialVendorId }: {
  open: boolean;
  onClose: () => void;
  onScheduled: (result: ScheduleResult, domain: string) => void;
  initialDomain?: string;
  initialVendorId?: string | null;
}) {
  const tt = useScanT();
  const credits = useCredits();
  const describeError = useScanError();
  const { allows } = useAuth();

  const [target, setTarget] = useState<string>(OWN);
  const [domainInput, setDomainInput] = useState('');
  const [frequency, setFrequency] = useState<ScanFrequency>('SINGLE');
  const [acknowledged, setAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [error]);

  // Reset every time the dialog opens, so a previous acknowledgement can never
  // carry over to a different target or price.
  useEffect(() => {
    if (!open) return;
    setTarget(initialVendorId ?? OWN);
    setDomainInput(initialVendorId ? '' : initialDomain ?? '');
    setFrequency('SINGLE');
    setAcknowledged(false);
    setError(null);
  }, [open, initialDomain, initialVendorId]);

  const pricing = useQuery({
    queryKey: ['scans', 'pricing', 'dialog'],
    queryFn: () => api.get<Pricing>('/scans/pricing'),
    enabled: open,
    // A price is never shown from cache: always re-read when the dialog opens.
    staleTime: 0,
    gcTime: 0,
  });

  const canReadVendors = allows('vendor.read');
  const vendors = useQuery({
    queryKey: ['scans', 'vendor-options'],
    queryFn: () => api.get<{ items: VendorOption[] }>(`/vendors${qs({ pageSize: 100 })}`),
    enabled: open && canReadVendors,
  });

  const vendor = vendors.data?.items.find((v) => v.id === target) ?? null;
  // A vendor selected before the list arrives (scan-again) must still resolve.
  const scanningVendor = target !== OWN;

  const domainCheck = useMemo(
    () => (domainInput.trim() ? checkRootDomain(domainInput) : null),
    [domainInput],
  );
  const effectiveDomain = scanningVendor
    ? (vendor?.domain ?? initialDomain ?? '')
    : normalizeDomain(domainInput);
  const domainError = !scanningVendor && domainCheck && !domainCheck.ok
    ? domainCheck.reason === 'SUBDOMAIN'
      ? tt('scans.new.domainSubdomain', { root: domainCheck.suggestedRoot })
      : tt('scans.new.domainInvalid')
    : undefined;
  const domainValid = scanningVendor ? effectiveDomain !== '' : domainCheck?.ok === true;

  const selected = pricing.data?.packages.find((p) => p.frequency === frequency) ?? null;

  const schedule = useMutation({
    mutationFn: () =>
      api.post<ScheduleResult>('/scans/schedule', {
        ...(scanningVendor ? { vendorId: target } : {}),
        domain: effectiveDomain,
        frequency,
        acknowledged: true,
      }),
    onSuccess: (result) => onScheduled(result, effectiveDomain),
    onError: (err) => {
      // A failed charge must not leave the acknowledgement armed: the person
      // re-reads the price and ticks again.
      setAcknowledged(false);
      setError(describeError(err));
    },
  });

  const canConfirm =
    !!selected && selected.affordable && acknowledged && domainValid && !schedule.isPending;

  const cost = selected?.totalCost ?? 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={tt('scans.new.title')}
      footer={
        <>
          <Button onClick={onClose}>{tt('scans.new.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!canConfirm}
            onClick={() => { setError(null); schedule.mutate(); }}
          >
            {schedule.isPending
              ? tt('scans.new.working')
              : selected
                ? tt(frequency === 'SINGLE' ? 'scans.new.confirmSingle' : 'scans.new.confirmSchedule', { credits: credits(cost) })
                : tt(pricing.isError ? 'scans.new.priceUnavailable' : 'scans.new.pricingLoading')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && (
          <div ref={errorRef}>
            <ErrorNotice message={error} />
          </div>
        )}

        <div className="rounded-xl bg-surface-sunken px-3 py-2.5 flex items-start gap-2">
          <IconShield className="w-4 h-4 text-brand shrink-0 mt-0.5" aria-hidden />
          <p className="text-xs text-ink-secondary leading-relaxed">
            {tt('scans.scope.does')} {tt('scans.scope.doesNot')}
          </p>
        </div>

        {/* --- Target ---------------------------------------------------- */}
        {canReadVendors && (
          <Field label={tt('scans.new.target')} htmlFor="scan-target">
            <select
              id="scan-target"
              className="oc-input"
              value={target}
              onChange={(e) => { setTarget(e.target.value); setAcknowledged(false); }}
            >
              <option value={OWN}>{tt('scans.new.targetOwn')}</option>
              {(vendors.data?.items ?? []).filter((v) => v.isActive || v.id === target).map((v) => (
                <option key={v.id} value={v.id}>{v.name} ({v.domain})</option>
              ))}
            </select>
            {vendors.isError && (
              <p role="alert" className="mt-1 text-xs text-critical">{tt('scans.new.vendorsFailed')}</p>
            )}
          </Field>
        )}

        {scanningVendor ? (
          <p className="font-mono text-sm text-ink break-all">{effectiveDomain}</p>
        ) : (
          <Field
            label={tt('scans.new.domain')}
            htmlFor="scan-domain"
            hint={tt('scans.new.domainHint')}
            error={domainError}
            required
          >
            <input
              id="scan-domain"
              className="oc-input font-mono"
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              placeholder="example.com"
              value={domainInput}
              onChange={(e) => { setDomainInput(e.target.value); setAcknowledged(false); }}
            />
            {domainCheck?.ok && (
              <p className="mt-1 text-xs text-ink-secondary">
                {tt('scans.new.willScan', { domain: effectiveDomain })}
              </p>
            )}
          </Field>
        )}
        <p className="text-xs text-ink-secondary -mt-2">
          {tt('scans.new.authorise')} {tt('scans.scope.bareDomain')}
        </p>

        {/* --- Frequency and price ---------------------------------------- */}
        <fieldset>
          <legend className="oc-label">{tt('scans.new.frequency')}</legend>
          {pricing.isPending && <Skeleton className="h-40 w-full" />}
          {pricing.isError && (
            <ErrorNotice
              message={tt('scans.new.pricingFailed')}
              onRetry={() => void pricing.refetch()}
              retryLabel={tt('scans.retry')}
            />
          )}
          {pricing.data && (
            <div className="space-y-2">
              {[...SCAN_FREQUENCIES]
                .sort((a, b) => (pricing.data.packages.find((p) => p.frequency === a)?.runs ?? 0)
                  - (pricing.data.packages.find((p) => p.frequency === b)?.runs ?? 0))
                .map((f) => {
                const pkg = pricing.data.packages.find((p) => p.frequency === f);
                if (!pkg) return null;
                const active = f === frequency;
                return (
                  <label
                    key={f}
                    className={cn(
                      'flex items-start gap-3 rounded-xl border px-3 py-3 cursor-pointer min-h-[44px]',
                      active ? 'border-brand bg-brand/[0.05]' : 'border-line',
                    )}
                  >
                    <input
                      type="radio"
                      name="scan-frequency"
                      className="mt-1 w-4 h-4 accent-[rgb(var(--brand))]"
                      checked={active}
                      onChange={() => { setFrequency(f); setAcknowledged(false); }}
                    />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-ink">
                        {tt(`scans.new.freq.${f}` as 'scans.new.freq.SINGLE')}
                        <span className="font-normal text-ink-secondary"> · {tt('scans.new.runs', { count: pkg.runs })}</span>
                      </span>
                      <span className="block text-sm text-ink tabular-nums">
                        {tt('scans.new.optionCost', { credits: credits(pkg.totalCost) })}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </fieldset>

        {pricing.data && selected && (
          <div className="rounded-xl bg-surface-sunken p-4 space-y-2 text-sm">
            <p className="font-medium text-ink">{tt('scans.new.summaryTitle')}</p>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-3">
                <dt className="text-ink-secondary">{tt('scans.new.balanceNow')}</dt>
                <dd className="tabular-nums text-ink">{credits(pricing.data.balance)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-ink-secondary">{tt('scans.new.chargedNow')}</dt>
                <dd className="tabular-nums font-semibold text-ink">
                  −{credits(selected.totalCost)}
                </dd>
              </div>
              <div className="flex justify-between gap-3 pt-2 border-t border-line">
                <dt className="text-ink-secondary">{tt('scans.new.balanceAfter')}</dt>
                <dd className={cn('tabular-nums font-medium', selected.affordable ? 'text-ink' : 'text-critical')}>
                  {credits(selected.balanceAfter)}
                </dd>
              </div>
            </dl>
            {!selected.affordable && (
              <p role="alert" className="text-sm text-critical">
                {tt('scans.new.shortfall', { shortfall: selected.totalCost - pricing.data.balance })}
              </p>
            )}
            <p className="text-xs text-ink-secondary">{tt('scans.new.noRefund')}</p>
          </div>
        )}

        {selected && selected.affordable && domainValid && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 w-4 h-4 accent-[rgb(var(--brand))] shrink-0"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span className="text-sm text-ink">
              {tt('scans.new.ack', {
                count: selected.runs,
                credits: credits(selected.totalCost),
                domain: effectiveDomain || '…',
              })}
            </span>
          </label>
        )}
      </div>
    </Modal>
  );
}
